import { useScrollDepth } from "@/hooks";
import { trackEvent } from "@/lib/dataLayer";
import {
  CookieConsent,
  DemoFormModal,
  Footer,
  Header,
  WhatsAppFloatButton,
} from "@/pages/landingPage/components";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { SeoSection } from "./components/seoSection";
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildServiceSchema,
  SITE_URL,
} from "./schemas";
import type { SeoLandingConfig } from "./types";

type SeoLandingProps = {
  config: SeoLandingConfig;
};

/**
 * Template das LPs de SEO (segmento × ferramenta). O conteúdo vem da config
 * em `lps/`; a ordem das sections é a da config.
 */
export const SeoLanding = ({ config }: SeoLandingProps) => {
  useScrollDepth();
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  const openDemo = (location: "header" | "hero" | "cta_band") => {
    trackEvent({ event: "cta_click_specialist", location });
    setIsDemoOpen(true);
  };

  const url = `${SITE_URL}${config.path}`;
  const ogTitle = `${config.title} | Fidexa`;
  const faq = config.sections.find(section => section.type === "faq");

  return (
    <>
      <Helmet>
        <title>{config.title}</title>
        <meta name="description" content={config.description} />
        <link rel="canonical" href={url} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fidexa" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={config.description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={config.description} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">
          {JSON.stringify(buildBreadcrumbSchema(config))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(buildServiceSchema(config))}
        </script>
        {faq ? (
          <script type="application/ld+json">
            {JSON.stringify(buildFaqSchema(faq.items, config.updatedAt))}
          </script>
        ) : null}
      </Helmet>

      <div className="bg-ds-paper min-h-screen">
        <Header onSpecialistClick={() => openDemo("header")} />
        <main className="flex flex-col gap-16 pb-16 md:gap-24">
          {config.sections.map(section => (
            <SeoSection
              key={"id" in section ? section.id : section.type}
              section={section}
              updatedAt={config.updatedAt}
              onDemoClick={openDemo}
            />
          ))}
        </main>
        <Footer />
        <WhatsAppFloatButton />
        <CookieConsent />
        <DemoFormModal
          isOpen={isDemoOpen}
          onClose={() => setIsDemoOpen(false)}
          title="Agende sua demonstração gratuita"
          description="Conte um pouco sobre sua operação. Um especialista entra em contato no WhatsApp para mostrar a Fidexa com a carteira da sua loja."
        />
      </div>
    </>
  );
};
